/**
 * THE FOUR THEME AXES, AS PURE DATA (DESIGN.md §16.2).
 *
 * Modus, bakgrunn, gjennomsiktighet og aksent. All four are local preferences — localStorage,
 * never the backend (00-contracts §14.1: «theme is local-only»). A value that is missing or not
 * one of the allowed ones reads as the default, so a hand-edited or stale key can never leave
 * the document without an attribute.
 *
 * The same rules are written a second time, in ES5, in the blocking script in `index.html`,
 * because that script runs before any module exists. `theme-bootstrap.spec.ts` runs that script
 * against `resolveAppearance()` for every combination, so the two cannot disagree.
 */
import {
  ACCENTS,
  BACKDROPS,
  DEFAULT_ACCENT,
  DEFAULT_BACKDROP,
  DEFAULT_GLASS,
  GLASS_LEVELS,
  type Accent,
  type Backdrop,
  type GlassLevel,
} from '@tvitvi/shared';

export type { Accent, Backdrop, GlassLevel };
export { ACCENTS, BACKDROPS, GLASS_LEVELS };

export type ThemeMode = 'light' | 'dark' | 'system';
export type ResolvedMode = 'light' | 'dark';
export const THEME_MODES: readonly ThemeMode[] = ['light', 'dark', 'system'];

export const STORAGE_KEYS = {
  theme: 'tvitvi.theme',
  backdrop: 'tvitvi.backdrop',
  glass: 'tvitvi.glass',
  accent: 'tvitvi.accent',
  followSystem: 'tvitvi.glass.followSystem',
} as const;

export const MEDIA = {
  dark: '(prefers-color-scheme: dark)',
  reducedTransparency: '(prefers-reduced-transparency: reduce)',
  moreContrast: '(prefers-contrast: more)',
} as const;

export interface AppearancePreferences {
  readonly mode: ThemeMode;
  readonly backdrop: Backdrop;
  readonly glass: GlassLevel;
  readonly accent: Accent;
  readonly followSystem: boolean;
}

/** «Tilbakestill til standard»: system, fjord (Atle 31.10), 2, blue, follow the system. */
export const DEFAULT_APPEARANCE: AppearancePreferences = {
  mode: 'system',
  backdrop: DEFAULT_BACKDROP,
  glass: DEFAULT_GLASS,
  accent: DEFAULT_ACCENT,
  followSystem: true,
};

export interface SystemSignals {
  readonly prefersDark: boolean;
  readonly reducedTransparency: boolean;
  readonly moreContrast: boolean;
}

export interface ResolvedAppearance {
  readonly theme: ResolvedMode;
  readonly backdrop: Backdrop;
  readonly glass: GlassLevel;
  readonly accent: Accent;
}

/** Stored strings in, valid preferences out. Anything unknown falls back to the default. */
export function parsePreferences(
  read: (key: string) => string | null,
): AppearancePreferences {
  const mode = read(STORAGE_KEYS.theme);
  const backdrop = read(STORAGE_KEYS.backdrop);
  const glass = read(STORAGE_KEYS.glass);
  const accent = read(STORAGE_KEYS.accent);
  const follow = read(STORAGE_KEYS.followSystem);
  return {
    mode: (THEME_MODES as readonly string[]).includes(mode ?? '')
      ? (mode as ThemeMode)
      : DEFAULT_APPEARANCE.mode,
    backdrop: (BACKDROPS as readonly string[]).includes(backdrop ?? '')
      ? (backdrop as Backdrop)
      : DEFAULT_APPEARANCE.backdrop,
    glass: (GLASS_LEVELS.map(String) as readonly string[]).includes(glass ?? '')
      ? (Number(glass) as GlassLevel)
      : DEFAULT_APPEARANCE.glass,
    accent: (ACCENTS as readonly string[]).includes(accent ?? '')
      ? (accent as Accent)
      : DEFAULT_APPEARANCE.accent,
    // Only an explicit «false» turns it off; the default is to let the system win.
    followSystem: follow !== 'false',
  };
}

/**
 * What is DRAWN. The user's choice stays stored; only the painted value changes when the system
 * asks (§16.2): reduced transparency → glass 0, more contrast → glass 0 and a flat backdrop, both
 * only while «følg systemet» is on (interpretation 4).
 */
export function resolveAppearance(
  preferences: AppearancePreferences,
  system: SystemSignals,
): ResolvedAppearance {
  const systemWins = preferences.followSystem;
  const glassOff = systemWins && (system.reducedTransparency || system.moreContrast);
  const flat = systemWins && system.moreContrast;
  return {
    theme:
      preferences.mode === 'system' ? (system.prefersDark ? 'dark' : 'light') : preferences.mode,
    backdrop: flat ? 'flat' : preferences.backdrop,
    glass: glassOff ? 0 : preferences.glass,
    accent: preferences.accent,
  };
}

/** The four root attributes, as `setAttribute` pairs. */
export function rootAttributes(resolved: ResolvedAppearance): Record<string, string> {
  return {
    'data-theme': resolved.theme,
    'data-backdrop': resolved.backdrop,
    'data-glass': String(resolved.glass),
    'data-accent': resolved.accent,
  };
}
